<template>
  <EModal
    :active="active"
    :full-width="true"
    class="take-action-modal"
    v-bind="$attrs"
    v-on="$listeners"
  >
    <div class="take-action-modal__content">
      <div class="take-action-modal__header take-action-modal__content-item">
        <EHeading as="h3" class="take-action-modal__headline">
          {{ headlineText }}
        </EHeading>
      </div>
      <div class="take-action-modal__body">
        <ECard class="share-card" :elevation="0">
          <div class="share-card__content">
            <div class="share-card__content-share-container">
              <div>
                <div class="share-card__content-action-text">
                  <p>
                    {{ contentText }}
                  </p>
                </div>
                <div class="share-card__content-share-icons">
                  <a
                    href="https://www.facebook.com/"
                    rel="noopener"
                    target="_blank"
                    @click="trackUserActionClick('facebook')"
                  >
                    <EIcon icon="facebook" size="l" />
                  </a>

                  <a
                    :href="twitterShareUrl"
                    rel="noopener"
                    target="_blank"
                    @click="trackUserActionClick('twitter')"
                  ><EIcon
                    icon="twitter"
                    size="l"
                  /></a>
                  <a
                    href="https://www.linkedin.com/"
                    rel="noopener"
                    target="_blank"
                    @click="trackUserActionClick('linkedin')"
                  ><EIcon
                    icon="linkedin"
                    size="l"
                  /></a>
                </div>
              </div>
              <div class="share-card__content-icon">
                <IconUsers />
              </div>
            </div>
            <button class="copy-button" @click="onCopyClick">
              <span class="copy-button__label">{{ shareUrl }}</span>
              <span class="copy-button__action">Copy link</span>
            </button>
          </div>
        </ECard>
      </div>
    </div>
    <EWave class="take-action-modal__footer-wave" aria-hidden="true" />
    <footer class="take-action-modal__footer">
      <ELink
        as="button"
        class="feedback-link-cmp"
        color="secondary"
        data-test-id="cp-feedback-link"
        @click="onFeedbackLinkClick"
      >
        {{ $t('common.footer.feedback') }}
      </ELink>
    </footer>
  </EModal>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EModal from '~~/common/vue/components/modal';
import EHeading from '~~/common/vue/components/heading';
import ELink from '~~/common/vue/components/link';
import ECard from '~~/common/vue/components/card';
import EIcon from '~~/common/vue/components/icon';
import {
  climatePledgeUserActionClick,
} from '~~/search/common/js/client/analytics/climate-pledge-ratings';

export default {
  name: 'EClimatePledgeModal',
  components: {
    EModal,
    EHeading,
    ELink,
    ECard,
    EIcon,
    EWave: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "take-action-modal-footer-wave" */
      './climate-pledge-modal/assets/climate-pledge-modal-footer-wave.svg'
    ),
    IconUsers: async () => import(
      /* webpackPrefetch: true */
      /* webpackChunkName: "take-action-modal-icon-users" */
      './climate-pledge-modal/assets/share-users.svg'
    ),
  },
  props: {
    active: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      feedbackActive: false,
    };
  },
  computed: {
    ...mapState('user', ['bingMarketCode']),
    ...mapGetters('unleash', ['getFeatureToggle']),
    headlineText() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ?
        'Spread the word' :
        "Here's what you can do";
    },
    contentText() {
      return this.getFeatureToggle('cpr_action_button_text')?.value === 'share' ?
        'Share this climate pledge rating to spark conversation' :
        'Share this climate pledge rating to spread the word';
    },
    twitterShareText() {
      return this.getFeatureToggle('cpr_action_twitter_text')?.value === 'only_link_ecosia' ?
        "Love that @ecosia is rating which companies are doing enough for the climate! Search with Ecosia to know who's pulling their weight (and plant trees while you're at it)" :
        'Love that @ecosia is rating which companies are doing enough for the climate! @amazon you need to do better.';
    },
    twitterShareUrl() {
      const encodedText = encodeURIComponent(this.twitterShareText);
      const encodedUrl = encodeURIComponent(this.shareUrl);
      return `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`;
    },
    shareUrl() {
      return 'ecosia.org/search?tt=6adb764b&q=amazon&cpr=1';
    },
  },
  methods: {
    onFeedbackLinkClick() {
      this.$emit('cp-feedback-link-click');
    },
    onCopyClick() {
      const input = document.createElement('input');
      input.setAttribute('value', this.shareUrl);
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      this.trackUserActionClick('link_copy');
    },
    trackUserActionClick(label) {
      this.$track(climatePledgeUserActionClick(label, 'action modal'));
    },
  },
};
</script>
<style lang="scss" scoped>
.take-action-modal__content {
  padding: 0 $space-m;
  cursor: default;

  @include desktop {
    padding: 0 $space-5l;
  }
}

.take-action-modal__header {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.take-action-modal__body {
  width: 100%;
  display: flex;
  flex-direction: column;
}

.take-action-modal__headline {
  font-size: $font-1l;
  font-weight: $font-weight-1l;
  color: var(--color-text-primary);
  margin-top: $space-1s;
}

.take-action-modal__footer-wave {
  width: 100%;
  height: auto;
  margin-top: $space-4l;
  color: var(--color-background-secondary);

  @include desktop {
    color: $white;
  }
}

.take-action-modal__footer {
  background: var(--color-background-secondary);
  padding: 0 $space-2l $space-1l $space-2l;
  border-radius: 0 0 $border-radius-l $border-radius-l;
  text-align: right;

  @include desktop {
    background: $white;
  }
}

.share-card {
  margin-top: $space-1l;
  background: var(--color-background-primary);
  border: 1px solid var(--color-background-quaternary);
  padding: $space-s;
}

.share-card__content {
  font-size: $font-l;
}

.share-card__content-share-container {
  display: flex;
  align-items: space-between;
}

.share-card__content-action-text > p {
  margin-top: 0;
  margin-right: 16px;
}

.share-card__content-share-icons svg {
  cursor: pointer;
  color: $c-gray-70;
  height: 40px;
  width: 40px;

  &:hover {
    color: $c-gray-50;
  }
}

.copy-button {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: $space-s $space-m;
  background: var(--color-background-secondary);
  border: 1px solid var(--color-form-border-default);
  border-radius: $space-l;
  width: 100%;
  margin-top: $space-m;
  color: var(--color-link-primary);
  font-size: $font-m;
  cursor: pointer;

  &:hover {
    background: var(--color-button-background-transparent-hover);

    .copy-button__action {
      text-decoration: underline;
    }
  }

  &:active {
    color: var(--color-button-tertiary-active);
    background: var(--color-button-background-transparent-active);
  }
}

.copy-button__label {
  color: var(--color-text-secondary);
}

.dark {
  .share-card__content-share-icons svg {
    color: $white;

    &:hover {
      color: $c-gray-30;
    }
  }

  .take-action-modal__footer {
    @include desktop {
      background: var(--color-background-quaternary);
    }
  }

  .take-action-modal__footer-wave {
    @include desktop {
      color: var(--color-background-quaternary);
    }
  }
}
</style>

<style lang="scss">
.take-action-modal .modal__dialog {
  background: var(--color-background-quaternary);

  @include desktop {
    background: var(--color-background-secondary);
  }
}
</style>
